import { useState } from 'react';
import {
    SelectWorkshop as SelectWorkshopType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { getValue } from '../../values/Get';
import { CheckboxGroup, CheckboxField, Link, FlosIcon } from '@flos/react-ui';
import CustomerVehicle from '../CustomerVehicle/CustomerVehicle';
import ConfirmationOverlay from './ConfirmationOverlay';
import useAppState from '../../../state/appState';
import './style.scss';
import { trackInteract } from '../../../track-by-submit/common/tracking';

export type SelectWorkshopOptionType = 'shopsNotYetSearchedFor' | 'customerDoesNotWantShops';
const OWN_SHOP_LABEL = 'Jeg kontakter selv et værksted';
const OWN_SHOP_VALUE_HUMAN = 'Du har valgt eget værksted';
const RECOMMEND_SHOP_LABEL = 'Anbefal et værksted for mig (værkstedet kontakter dig inden for 2 timer)';
const customerVehicleElementID = 'registrationNumber';

const SelectWorkshop = ({ caption, elementId }: SelectWorkshopType) => {
    const formValues = useAppState((state) => state.formValues);
    console.log('formValues', JSON.stringify(formValues));
    const formConfig = useAppState((state) => state.formConfig);
    const setFormValue = useAppState((state) => state.setFormValue);
    const value = SelectWorkshop.getValueOrDefault(formValues, elementId);
    const customerVehicle = CustomerVehicle.getValueOrDefault(formValues, customerVehicleElementID);
    const brand = customerVehicle.displayValue;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const onChangeValue = (action?: SelectWorkshopOptionType) => {
        setFormValue(elementId, { scenario: action, brand });
    };

    if (!formConfig) {
        return <></>;
    }

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <div className="select-workshop-info">
                <h6 className="list-header">Du får disse fordele</h6>
                <ul className="info-list">
                    <li>Garanteret høj kvalitet</li>
                    <li>Udvidet garanti på reparationen</li>
                    <li>Lånebil uden beregning, mens din egen bil er på værksted</li>
                    <li>
                        I dele af landet kan du også vælge drive-in-værkstedet Carbucks, hvor reparationen kun tager to
                        timer
                    </li>
                </ul>
            </div>
            <div className="select-workshop">
                <CheckboxGroup
                    id={elementId}
                    key={`checkbox-group-${value.scenario}`}
                    label="Bare book med det samme"
                    description="Hvis du har en kaskoforsikring, kan du allerede nu booke tid hos et værksted. Du kan altid ombestemme dig, hvis du ændrer planer."
                >
                    <CheckboxField
                        id={`${elementId}-checkbox`}
                        key={`checkbox-shopsNotYetSearchedFor`}
                        label={RECOMMEND_SHOP_LABEL}
                        value={'shopsNotYetSearchedFor'}
                        onChange={({ target: { value, checked } }) => {
                            onChangeValue(checked ? (value as SelectWorkshopOptionType) : undefined);
                        }}
                        checked={
                            value.scenario === 'shopsNotYetSearchedFor' ||
                            value.scenario === 'shopsFoundAndShopTypeChosenAndShopChosen'
                        }
                    />
                </CheckboxGroup>
                <div className="own-workshop-link">
                    Nej tak, jeg ønsker selv at kontakte et værksted -{' '}
                    <Link
                        onClick={() => {
                            trackInteract(formConfig, [{ key: 'workshop-selection-overlay', value: 'true' }]);
                            setIsModalOpen(true);
                        }}
                    >
                        klik her
                    </Link>
                </div>
                {value.scenario === 'customerDoesNotWantShops' && (
                    <div className="own-workshop-note">
                        <FlosIcon shape="check" size={24} />
                        <span>{OWN_SHOP_LABEL}</span>
                    </div>
                )}
                <ConfirmationOverlay
                    open={isModalOpen}
                    close={() => {
                        setIsModalOpen(false);
                    }}
                    onSkipWorkshopSelection={(action) => {
                        trackInteract(formConfig as FormConfig, [
                            {
                                key: `workshop-selection-action-${
                                    action === 'customerDoesNotWantShops' ? 'skip-workshop' : 'choose-workshop'
                                }`,
                                value: 'true'
                            }
                        ]);
                        onChangeValue(action as SelectWorkshopOptionType);
                    }}
                />
            </div>
        </>
    );
};

SelectWorkshop.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as SelectWorkshopType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    const { scenario } = value;

    if (value === undefined) {
        return <></>;
    }

    const displayValue = scenario === 'customerDoesNotWantShops' ? OWN_SHOP_LABEL : RECOMMEND_SHOP_LABEL;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={displayValue} />;
};

SelectWorkshop.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue => {
    const valueObj = SelectWorkshop.getValue(formValues, elementId);
    const valueStr = SelectWorkshop.getValueHuman(formValues, elementId);

    const { scenario, brand } = valueObj;
    let valueId = {};
    let name = elementId;
    if (scenario === 'customerDoesNotWantShops') {
        valueId = {
            scenario,
            carBrand: brand,
            branchId: '',
            branchName: '',
            noShopsAction: 'customerDoesNotWantToChooseBetweenPresentedOptions',
            searchQuery: '',
            chosenShopType: '',
            carbucksShops: [],
            regularShops: [],
            componentState: 'ok'
        };
        name = 'supManWidget';
    }

    return getClaimFormSubmitValue('supmanwidget', name, caption, valueStr, valueId);
};

SelectWorkshop.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value = SelectWorkshop.getValue(formValues, elementId);
    const { scenario } = value;
    if (scenario === 'customerDoesNotWantShops') {
        return OWN_SHOP_VALUE_HUMAN;
    }

    return scenario;
};

SelectWorkshop.getValueOrDefault = (formValues: FormValues, elementId: string): any => {
    const valueObj: any = SelectWorkshop.getValue(formValues, elementId);

    if (valueObj === undefined) {
        return {
            scenario: ''
        };
    }

    return valueObj;
};

SelectWorkshop.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (valueObject && valueObject.scenario !== undefined) {
        return true;
    }

    return false;
};

SelectWorkshop.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

export default SelectWorkshop;
